import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import CollapsibleFilters from './CollapsibleFilters';

interface Reservation {
  id: number;
  stock_id: number;
  product_id: number;
  sku: string;
  product_name: string;
  quantity_reserved: number;
  quantity_available: number;
  reason: string;
  date: string;
  total_reserved_on_sku: number;
}

const ReservationManager: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [warehouses, setWarehouses] = useState<Array<{id: number; code: string; name: string; is_default?: boolean}>>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>(0);
  
  // New reservation form
  const [skuSearch, setSkuSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{id: number; sku: string; name: string; stock_id: number; available: number}>>([]);
  const [selectedStock, setSelectedStock] = useState<{stock_id: number; sku: string; name: string; available: number} | null>(null);
  const [reserveQty, setReserveQty] = useState('');
  const [reserveReason, setReserveReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Release
  const [releaseTarget, setReleaseTarget] = useState<Reservation | null>(null);
  const [releaseQty, setReleaseQty] = useState('');
  const [releaseReason, setReleaseReason] = useState('');

  useEffect(() => { fetchReservations(); fetchWarehouses(); }, []);

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/inventory/warehouses/');
      const whs = res.data || [];
      setWarehouses(whs);
      const defaultWh = whs.find((w: any) => w.is_default);
      if (defaultWh) setSelectedWarehouseId(defaultWh.id);
      else if (whs.length > 0) setSelectedWarehouseId(whs[0].id);
    } catch (e) {}
  };

  const filteredReservations = useMemo(() => {
    if (!searchText) return reservations;
    const q = searchText.toLowerCase();
    return reservations.filter(r =>
      (r.sku || '').toLowerCase().includes(q) ||
      (r.product_name || '').toLowerCase().includes(q) ||
      (r.reason || '').toLowerCase().includes(q)
    );
  }, [reservations, searchText]);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventory/reservations');
      setReservations(res.data || []);
    } catch (error) {
      console.error('Failed to load reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkuSearch = async (value: string) => {
    setSkuSearch(value);
    setSelectedStock(null);
    if (value.length < 2) { setSearchResults([]); return; }
    
    try {
      const params: any = { q: value, limit: 10 };
      if (selectedWarehouseId) params.warehouse_id = selectedWarehouseId;
      const res = await api.get('/inventory/stock/', { params });
      const stocks = res.data || [];
      setSearchResults(stocks.map((s: any) => ({
        stock_id: s.id,
        sku: s.product?.sku || '',
        name: s.product?.name || '',
        available: s.quantity_available
      })));
    } catch (error) {
      setSearchResults([]);
    }
  };

  const handleReserveSubmit = async () => {
    if (!selectedStock || !reserveQty || !reserveReason.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/inventory/reserve', {
        stock_id: selectedStock.stock_id,
        quantity: parseInt(reserveQty),
        reason: reserveReason.trim()
      });
      setSelectedStock(null);
      setSkuSearch('');
      setReserveQty('');
      setReserveReason('');
      setSearchResults([]);
      fetchReservations();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Reservation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async () => {
    if (!releaseTarget || !releaseQty) return;
    setSubmitting(true);
    try {
      await api.post('/inventory/release-reservation', {
        stock_id: releaseTarget.stock_id,
        quantity: parseInt(releaseQty),
        reason: releaseReason.trim() || 'Released'
      });
      setReleaseTarget(null);
      setReleaseQty('');
      setReleaseReason('');
      fetchReservations();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Release failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="card-header" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ fontSize: '16px' }}>Inventory Reservations</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by SKU, product, reason..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
      </div>

      {/* Quick Reserve Section */}
      <CollapsibleFilters label="Quick Reserve">
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f3f5', background: '#fafbfc' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', color: '#475569' }}>Quick Reserve</div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: '180px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '4px' }}>Warehouse</label>
            <select
              value={selectedWarehouseId}
              onChange={(e) => { setSelectedWarehouseId(parseInt(e.target.value)); setSelectedStock(null); setSkuSearch(''); setSearchResults([]); }}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
            >
              <option value={0}>All Warehouses</option>
              {warehouses.map(wh => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '4px' }}>Search by SKU or Product Name</label>
            <input
              type="text"
              value={skuSearch}
              onChange={(e) => handleSkuSearch(e.target.value)}
              placeholder="Type SKU or product name..."
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
            />
            {searchResults.length > 0 && !selectedStock && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '200px', overflow: 'auto', marginTop: '4px' }}>
                {searchResults.map(r => (
                  <button
                    key={r.stock_id}
                    onClick={() => { setSelectedStock(r); setSearchResults([]); setSkuSearch(r.sku); }}
                    style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#f8fafb')}
                    onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    <strong>{r.sku}</strong> — {r.name} <span style={{ color: '#64748b', marginLeft: '8px' }}>({r.available} avail)</span>
                  </button>
                ))}
              </div>
            )}
            {selectedStock && (
              <div style={{ fontSize: '12px', color: '#0d9488', marginTop: '4px' }}>
                ✓ {selectedStock.name} — {selectedStock.available} available
              </div>
            )}
          </div>
          <div style={{ width: '100px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '4px' }}>Qty</label>
            <input
              type="number"
              value={reserveQty}
              onChange={(e) => setReserveQty(e.target.value)}
              min="1"
              max={selectedStock?.available || 999}
              placeholder="Qty"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '180px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '4px' }}>Reason</label>
            <input
              type="text"
              value={reserveReason}
              onChange={(e) => setReserveReason(e.target.value)}
              placeholder="e.g., Client demo, Exhibition..."
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleReserveSubmit}
            disabled={submitting || !selectedStock || !reserveQty || parseInt(reserveQty) <= 0 || !reserveReason.trim()}
            style={{ padding: '8px 16px' }}
          >
            {submitting ? 'Reserving...' : 'Reserve'}
          </button>
        </div>
      </div>
      </CollapsibleFilters>

      {/* Active Reservations List */}
      <div className="table-container" style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : reservations.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No active reservations</div>
        ) : filteredReservations.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No reservations match your search</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th>Reason</th>
                <th>Date</th>
                <th style={{ textAlign: 'center' }}>Total Reserved</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservations.map(r => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '12px' }}>{r.sku}</td>
                  <td>{r.product_name}</td>
                  <td style={{ textAlign: 'center' }}>
                    <strong style={{ color: '#f59e0b', fontSize: '14px' }}>{r.quantity_reserved}</strong>
                  </td>
                  <td style={{ fontSize: '12px', color: '#475569', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason}>
                    {r.reason}
                  </td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{r.date ? new Date(r.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ textAlign: 'center', color: '#64748b' }}>{r.total_reserved_on_sku}</td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      onClick={() => { setReleaseTarget(r); setReleaseQty(String(r.quantity_reserved)); setReleaseReason(''); }}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      Release
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Release Modal */}
      {releaseTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '420px' }}>
            <button className="modal-close" onClick={() => setReleaseTarget(null)} aria-label="Close">×</button>
            <h3>Release Reservation</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '8px 0 16px' }}>
              <strong>{releaseTarget.product_name}</strong> ({releaseTarget.sku})<br/>
              Currently reserved: <strong style={{ color: '#f59e0b' }}>{releaseTarget.quantity_reserved}</strong> units
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: '#475569' }}>
                Quantity to Release
              </label>
              <input
                type="number"
                value={releaseQty}
                onChange={(e) => setReleaseQty(e.target.value)}
                min="1"
                max={releaseTarget.quantity_reserved}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: '#475569' }}>
                Reason (optional)
              </label>
              <input
                type="text"
                value={releaseReason}
                onChange={(e) => setReleaseReason(e.target.value)}
                placeholder="e.g., Demo completed, Event over..."
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-cancel" onClick={() => setReleaseTarget(null)} disabled={submitting}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleRelease}
                disabled={submitting || !releaseQty || parseInt(releaseQty) <= 0 || parseInt(releaseQty) > releaseTarget.quantity_reserved}
              >
                {submitting ? 'Releasing...' : 'Release Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationManager;
